import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  alpha,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBack from "@mui/icons-material/ArrowBack";
import CreditCard from "@mui/icons-material/CreditCard";
import Edit from "@mui/icons-material/Edit";
import ToggleOn from "@mui/icons-material/ToggleOn";
import ToggleOff from "@mui/icons-material/ToggleOff";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import {
  useGetPlanDetail,
  useUpdatePlan,
  useGetPlanPricing,
  useCreatePlanPricing,
} from "../../hooks/api/usePlan";
import { useGetBillingCycleList } from "../../hooks/api/useBillingCycle";
import {
  useGetFeatureList,
  useGetPlanFeatures,
  useAddPlanFeature,
  useDeletePlanFeature,
} from "../../hooks/api/useFeature";
import type { Plan } from "../../Graphql/types/plan";
import type { PlanPricingItem } from "../../Graphql/types/plan";
import type { Feature } from "../../Graphql/types/feature";
import type { BillingCycle } from "../../Graphql/types/billingCycle";
import { ROUTE_MEMBERSHIP } from "../../constants/routes";
import { responsiveHeading } from "../../styles/layoutStyles";
import { formatBusinessTypeLabel } from "../common/adminEnumLabels";
import {
  ADMIN_PLAN_PRICING_COUNTRIES,
  currencyForAdminCountry,
} from "./constants/adminPlanPricingCountries";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch {
    return iso;
  }
}

/** Backend defaults omitted countryCode to RW; treat null/undefined the same. */
function resolvePricingCountryCode(p: PlanPricingItem): string {
  return (
    p.countryCode ??
    ADMIN_PLAN_PRICING_COUNTRIES.find((c) => c.currency === p.priceCurrency)
      ?.code ??
    "RW"
  );
}

function DetailItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontFamily: mono ? "monospace" : undefined,
          fontSize: mono ? "0.875rem" : undefined,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

const PlanDetailView: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmStatusOpen, setConfirmStatusOpen] = useState(false);
  const [pendingActive, setPendingActive] = useState<boolean | null>(null);

  // Edit form state
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [pricings, setPricings] = useState<PlanPricingItem[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [billingCycles, setBillingCycles] = useState<BillingCycle[]>([]);
  const [allFeatures, setAllFeatures] = useState<Feature[]>([]);
  const [newPricingBillingCycleId, setNewPricingBillingCycleId] = useState("");
  const [newPricingCountryCode, setNewPricingCountryCode] = useState("RW");
  const [newPricingAmount, setNewPricingAmount] = useState("");
  const [newPricingCurrency, setNewPricingCurrency] = useState("RWF");
  const [newFeatureCode, setNewFeatureCode] = useState("");

  const getPlanDetail = useGetPlanDetail();
  const updatePlan = useUpdatePlan();
  const getPlanPricing = useGetPlanPricing();
  const createPlanPricing = useCreatePlanPricing();
  const getBillingCycleList = useGetBillingCycleList();
  const getFeatureList = useGetFeatureList();
  const getPlanFeatures = useGetPlanFeatures();
  const addPlanFeature = useAddPlanFeature();
  const deletePlanFeature = useDeletePlanFeature();

  useEffect(() => {
    if (!planId) return;
    getPlanDetail
      .mutateAsync({ planId })
      .then((res) => {
        const p = res.getPlan?.plan ?? null;
        setPlan(p);
        if (p) {
          setEditName(p.name);
          setEditDescription(p.description ?? "");
        }
      })
      .catch(() => {});
  }, [planId]);

  useEffect(() => {
    getBillingCycleList
      .mutateAsync({ activeOnly: false })
      .then((res) => {
        setBillingCycles(res.listBillingCycles?.billingCycleList ?? []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getFeatureList
      .mutateAsync({ activeOnly: false })
      .then((res) => {
        setAllFeatures(res.listFeatures?.featureList ?? []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!planId) return;
    getPlanPricing
      .mutateAsync({ planId })
      .then((res) => {
        setPricings(res.getPlanPricing?.planPricingList ?? []);
      })
      .catch(() => {});
  }, [planId]);

  useEffect(() => {
    if (!planId) return;
    getPlanFeatures
      .mutateAsync({ planId })
      .then((res) => {
        setFeatures(res.getPlanFeatures?.featureList ?? []);
      })
      .catch(() => {});
  }, [planId]);

  const openEdit = () => {
    if (plan) {
      setEditName(plan.name);
      setEditDescription(plan.description ?? "");
      setEditOpen(true);
      if (planId) {
        getPlanPricing
          .mutateAsync({ planId })
          .then((res) => {
            setPricings(res.getPlanPricing?.planPricingList ?? []);
          })
          .catch(() => {});
        getPlanFeatures
          .mutateAsync({ planId })
          .then((res) => {
            setFeatures(res.getPlanFeatures?.featureList ?? []);
          })
          .catch(() => {});
      }
    }
  };

  const handleSaveEdit = () => {
    if (!planId) return;
    updatePlan
      .mutateAsync({
        planId,
        input: {
          name: editName,
          description: editDescription || undefined,
        },
      })
      .then((res) => {
        const updated = res.updatePlan?.plan;
        if (updated) setPlan(updated);
        setEditOpen(false);
      })
      .catch(() => {});
  };

  const handleCountryChange = (code: string) => {
    setNewPricingCountryCode(code);
    setNewPricingCurrency(currencyForAdminCountry(code));
  };

  const handleAddPricing = () => {
    if (
      !planId ||
      !newPricingBillingCycleId ||
      !newPricingCountryCode ||
      !newPricingAmount.trim()
    )
      return;
    const amount = parseFloat(newPricingAmount);
    if (Number.isNaN(amount)) return;
    createPlanPricing
      .mutateAsync({
        planId,
        billingCycleId: newPricingBillingCycleId,
        priceAmount: amount,
        priceCurrency: newPricingCurrency || undefined,
        countryCode: newPricingCountryCode,
      })
      .then((res) => {
        if (!res.createPlanPricing?.planPricing) return;
        getPlanPricing
          .mutateAsync({ planId })
          .then((pricingRes) => {
            setPricings(pricingRes.getPlanPricing?.planPricingList ?? []);
          })
          .catch(() => {});
        setNewPricingBillingCycleId("");
        setNewPricingAmount("");
      })
      .catch(() => {});
  };

  const handleAddFeature = () => {
    if (!planId || !newFeatureCode) return;
    addPlanFeature
      .mutateAsync({ planId, featureCode: newFeatureCode })
      .then(() => {
        getPlanFeatures
          .mutateAsync({ planId })
          .then((res) => {
            setFeatures(res.getPlanFeatures?.featureList ?? []);
          })
          .catch(() => {});
        setNewFeatureCode("");
      })
      .catch(() => {});
  };

  const handleRemoveFeature = (featureCode: string) => {
    if (!planId) return;
    deletePlanFeature
      .mutateAsync({ planId, featureCode })
      .then(() => {
        setFeatures((prev) =>
          prev.filter((f) => f.featureCode !== featureCode),
        );
      })
      .catch(() => {});
  };

  const canAddMorePricing = billingCycles.some((bc) =>
    ADMIN_PLAN_PRICING_COUNTRIES.some(
      (c) =>
        !pricings.some(
          (p) =>
            p.billingCycleId === bc.billingCycleId &&
            resolvePricingCountryCode(p) === c.code,
        ),
    ),
  );
  const countriesAvailableForSelectedCycle =
    ADMIN_PLAN_PRICING_COUNTRIES.filter(
      (c) =>
        !pricings.some(
          (p) =>
            p.billingCycleId === newPricingBillingCycleId &&
            resolvePricingCountryCode(p) === c.code,
        ),
    );
  const featuresNotOnPlan = allFeatures.filter(
    (f) => !features.some((pf) => pf.featureCode === f.featureCode),
  );

  const openConfirmStatus = () => {
    if (!plan) return;
    setPendingActive(!plan.isActive);
    setConfirmStatusOpen(true);
  };

  const pendingStatus = pendingActive;

  const handleConfirmStatus = () => {
    if (!planId || pendingStatus == null) return;
    updatePlan
      .mutateAsync({
        planId,
        input: { isActive: pendingStatus },
      })
      .then((res) => {
        const updated = res.updatePlan?.plan;
        if (updated) setPlan(updated);
        setConfirmStatusOpen(false);
        setPendingActive(null);
      })
      .catch(() => {});
  };

  const handleCloseConfirmStatus = () => {
    setConfirmStatusOpen(false);
    setPendingActive(null);
  };

  if (!planId) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography color="text.secondary">Missing plan ID.</Typography>
      </Box>
    );
  }

  if (!getPlanDetail.isPending && !plan) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography color="text.secondary">Plan not found.</Typography>
        <Button
          startIcon={<ArrowBack />}
          sx={{ mt: 1 }}
          onClick={() => navigate(ROUTE_MEMBERSHIP)}
        >
          Back to membership
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <IconButton
        onClick={() => navigate(ROUTE_MEMBERSHIP)}
        sx={{ mb: 2, color: "text.secondary" }}
        aria-label="Back to membership"
      >
        <ArrowBack />
      </IconButton>

      {plan && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${theme.palette.background.paper} 100%)`,
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
              justifyContent="space-between"
            >
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                minWidth={0}
                flex={1}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CreditCard sx={{ fontSize: 32 }} />
                </Box>
                <Box minWidth={0}>
                  <Typography
                    variant="h5"
                    sx={{ ...responsiveHeading, mb: 0.5 }}
                  >
                    {plan.name}
                  </Typography>
                  <Chip
                    label={plan.isActive ? "Active" : "Inactive"}
                    size="small"
                    color={plan.isActive ? "success" : "default"}
                    sx={{ fontWeight: 600, textTransform: "uppercase" }}
                  />
                </Box>
              </Box>

              <Box
                display="flex"
                gap={1.5}
                flexWrap="wrap"
                justifyContent="flex-end"
                sx={{ flexShrink: 0 }}
              >
                <Button
                  variant="contained"
                  size="medium"
                  color={plan.isActive ? "warning" : "primary"}
                  startIcon={plan.isActive ? <ToggleOff /> : <ToggleOn />}
                  onClick={openConfirmStatus}
                  disabled={updatePlan.isPending}
                >
                  {plan.isActive ? "Deactivate" : "Activate"}
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<Edit />}
                  onClick={openEdit}
                >
                  Edit
                </Button>
              </Box>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Plan details
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              gap={3}
            >
              <DetailItem label="Name" value={plan.name} />
              <DetailItem
                label="Business type"
                value={formatBusinessTypeLabel(plan.businessType)}
              />
              <DetailItem label="Plan ID" value={plan.planId} mono />
              <Box sx={{ gridColumn: { xs: 1, sm: "1 / -1" } }}>
                <DetailItem
                  label="Description"
                  value={plan.description ?? "—"}
                />
              </Box>
              <DetailItem label="Created" value={formatDate(plan.createdAt)} />
              <DetailItem label="Updated" value={formatDate(plan.updatedAt)} />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Plan pricings
              </Typography>
              {pricings.length > 0 ? (
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {pricings.map((pp) => (
                    <Chip
                      key={pp.planPricingId}
                      label={`${pp.billingCycleCode} · ${resolvePricingCountryCode(pp)}: ${pp.priceAmount} ${pp.priceCurrency}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No pricings. Add them in Edit.
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Plan features
              </Typography>
              {features.length > 0 ? (
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {features.map((f) => (
                    <Chip
                      key={f.featureCode}
                      label={f.name}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No features. Add them in Edit.
                </Typography>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* Edit modal */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Edit plan</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
            />

            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>
              Plan pricings
            </Typography>
            {pricings.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {pricings.map((pp) => (
                  <Chip
                    key={pp.planPricingId}
                    label={`${pp.billingCycleCode} · ${resolvePricingCountryCode(pp)}: ${pp.priceAmount} ${pp.priceCurrency}`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
            {canAddMorePricing && (
              <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Billing cycle</InputLabel>
                  <Select
                    value={newPricingBillingCycleId}
                    label="Billing cycle"
                    onChange={(e) => {
                      const cycleId = e.target.value;
                      setNewPricingBillingCycleId(cycleId);
                      const available = ADMIN_PLAN_PRICING_COUNTRIES.filter(
                        (c) =>
                          !pricings.some(
                            (p) =>
                              p.billingCycleId === cycleId &&
                              resolvePricingCountryCode(p) === c.code,
                          ),
                      );
                      const nextCountry =
                        available.find((c) => c.code === newPricingCountryCode)
                          ?.code ?? available[0]?.code;
                      if (nextCountry) {
                        setNewPricingCountryCode(nextCountry);
                        setNewPricingCurrency(
                          currencyForAdminCountry(nextCountry),
                        );
                      }
                    }}
                  >
                    {billingCycles.map((bc) => (
                      <MenuItem
                        key={bc.billingCycleId}
                        value={bc.billingCycleId}
                      >
                        {bc.label} ({bc.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={newPricingCountryCode}
                    label="Country"
                    onChange={(e) => handleCountryChange(e.target.value)}
                  >
                    {(newPricingBillingCycleId
                      ? countriesAvailableForSelectedCycle
                      : ADMIN_PLAN_PRICING_COUNTRIES
                    ).map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.label} ({c.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Amount"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={newPricingAmount}
                  onChange={(e) => setNewPricingAmount(e.target.value)}
                  sx={{ width: 120 }}
                />
                <TextField
                  size="small"
                  label="Currency"
                  value={newPricingCurrency}
                  InputProps={{ readOnly: true }}
                  helperText="From country"
                  sx={{ width: 100 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleAddPricing}
                  disabled={
                    !newPricingBillingCycleId ||
                    !newPricingCountryCode ||
                    !newPricingAmount.trim() ||
                    createPlanPricing.isPending ||
                    (Boolean(newPricingBillingCycleId) &&
                      countriesAvailableForSelectedCycle.length === 0)
                  }
                >
                  Add pricing
                </Button>
              </Box>
            )}

            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>
              Plan features
            </Typography>
            {features.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {features.map((f) => (
                  <Chip
                    key={f.featureCode}
                    label={f.name}
                    size="small"
                    variant="outlined"
                    onDelete={() => handleRemoveFeature(f.featureCode)}
                    deleteIcon={<Delete fontSize="small" />}
                  />
                ))}
              </Box>
            )}
            {featuresNotOnPlan.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Feature</InputLabel>
                  <Select
                    value={newFeatureCode}
                    label="Feature"
                    onChange={(e) => setNewFeatureCode(e.target.value)}
                  >
                    {featuresNotOnPlan.map((f) => (
                      <MenuItem key={f.featureCode} value={f.featureCode}>
                        {f.name} ({f.featureCode})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleAddFeature}
                  disabled={!newFeatureCode || addPlanFeature.isPending}
                >
                  Add feature
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={updatePlan.isPending}
          >
            {updatePlan.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm activate/deactivate */}
      <Dialog
        open={confirmStatusOpen}
        onClose={handleCloseConfirmStatus}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {pendingStatus ? "Activate plan?" : "Deactivate plan?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {plan &&
              (pendingStatus
                ? `Are you sure you want to activate "${plan.name}"?`
                : `Are you sure you want to deactivate "${plan.name}"?`)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseConfirmStatus} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pendingStatus ? "primary" : "warning"}
            onClick={handleConfirmStatus}
            disabled={updatePlan.isPending}
          >
            {updatePlan.isPending
              ? "Updating…"
              : pendingStatus
                ? "Activate"
                : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanDetailView;
